import React, { FC } from 'react';
import styled from 'styled-components';
import numbro from 'numbro';

import { translations } from 'i18n';
import { StarIcon, StarGroup } from './Stars';
import App from '../../../models/app';

const lightColor = '#707C83';
const size = 12;

const RatingsWrapper = styled.div`
  display: flex;
  align-items: center;

  font-family: ${({ theme }) => theme.fontLight};
  height: ${size}px;
  padding-top: 2px;
`;

const LargeNumber = styled.div`
  margin-right: 12px;
  font-family: ${({ theme }) => theme.fontBold};
  font-size: 56px;
  font-weight: 700;
  line-height: 0.67;
  letter-spacing: -1.96px;
`;

const BottomText = styled.div`
  font-size: 14px;
  margin-top: 4px;
  color: ${lightColor};
`;

const StarsPlusCount = styled.div`
  flex: 1;
`;

const InstallCount = styled.div`
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 16px;
`;

const InstallCountWrapper = styled.div`
  display: none;
  text-align: right;
`;

interface RatingsProps {
  app: App;
}

const Ratings: FC<RatingsProps> = ({ app }) => {
  const {
    component: {
      Ratings: { noRatingsText, numberOfRatingsText, installsText }
    }
  } = translations;
  let averageStars = 0;
  let numRatings = 0;
  if (app.ratingSummary && app.ratingSummary.numRatings > 0) {
    averageStars = app.ratingSummary.averageStars;
    numRatings = numbro(app.ratingSummary.numRatings).format({
      thousandSeparated: true
    });
  }

  return (
    <RatingsWrapper>
      {averageStars > 0 && (
        <LargeNumber id="ratings-average" aria-label="ratings-average">
          {numbro(averageStars).format({ mantissa: 1 })}
        </LargeNumber>
      )}
      <StarsPlusCount>
        <StarGroup filledStars={averageStars} />
        <BottomText id="ratings-count" aria-label="ratings-count">
          {numRatings === 0
            ? noRatingsText()
            : numberOfRatingsText({ numRatings })}
        </BottomText>
      </StarsPlusCount>
      <InstallCountWrapper>
        <InstallCount>0</InstallCount>
        <BottomText>{installsText()}</BottomText>
      </InstallCountWrapper>
    </RatingsWrapper>
  );
};

export default Ratings;

interface SimpleStarProps {
  app: App;
}

const SimpleStar: FC<SimpleStarProps> = (props) => <StarIcon size={18} {...props} />;

interface SimpleRatingsProps {
  app: App;
}

export const SimpleRatings: FC<SimpleRatingsProps> = ({ app }) => {
  if (
    app.showRatings &&
    app.ratingSummary &&
    app.ratingSummary.numRatings > 0
  ) {
    return (
      <RatingsWrapper>
        <StarGroup
          filledStars={app.ratingSummary.averageStars}
          Star={SimpleStar}
        />
      </RatingsWrapper>
    );
  }

  return null;
};
